import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Testids } from '@hweeks/kei-costs-ui-testids';
import { useEffect, useRef, useState } from 'react';
import { Button, ButtonLink, Caps, Card, Container, SectionLabel } from '../../design';
import { pagePricing, priced, pricingText } from '../../lib/pricing';
import { AnalyticsEvent, ctaEvent, track } from '../../utils/analytics';
import { CtaBand } from './CtaBand';
import { INCLUDED, THEMES } from './DealerWebsitesPage.data';
import { MKT } from './links';
import { MarketingLayout } from './MarketingLayout';
import { NextLink, ProductFaq, ProductHero, ProductSection } from './ProductParts';
import './dealer-websites.css';
function themeStyle(t) {
    return {
        '--th-ground': t.colors.ground,
        '--th-ink': t.colors.ink,
        '--th-accent': t.colors.accent,
        '--th-accent2': t.colors.accent2
    };
}
export default function DealerWebsitesPage() {
    const t = pricingText(pagePricing());
    const [preview, setPreview] = useState(null);
    const open = (theme)=>{
        setPreview({
            theme,
            url: `/site-previews/${theme.id}`
        });
        track(AnalyticsEvent.sitePreviewOpened, {
            theme: theme.id
        });
    };
    return /*#__PURE__*/ _jsxs(MarketingLayout, {
        testid: Testids.app.marketing.dealerWebsitesRoot,
        children: [
            /*#__PURE__*/ _jsxs("main", {
                children: [
                    /*#__PURE__*/ _jsx(ProductHero, {
                        k: "網",
                        label: "Add-on · Dealer websites",
                        title: "A dealer website that stocks itself.",
                        lead: "Pick one of five themes made for import dealers. We build it in your name and colors, host it, and keep it in sync with Right Hand DMS. Cars go up when they’re ready, and leads and deposits come back to you.",
                        actions: /*#__PURE__*/ _jsxs(_Fragment, {
                            children: [
                                /*#__PURE__*/ _jsx(ButtonLink, {
                                    href: "#themes",
                                    children: "See the themes"
                                }),
                                /*#__PURE__*/ _jsx(ButtonLink, {
                                    href: "#price",
                                    variant: "secondary",
                                    children: "What it costs"
                                })
                            ]
                        }),
                        children: /*#__PURE__*/ _jsxs("div", {
                            className: "mk-dw-sync",
                            children: [
                                /*#__PURE__*/ _jsxs("div", {
                                    className: "mk-pp-mock-head",
                                    children: [
                                        /*#__PURE__*/ _jsx("span", {
                                            className: "mk-dw-sync__title",
                                            children: "Car 1042 · 1999 Subaru Sambar 4WD"
                                        }),
                                        /*#__PURE__*/ _jsx("span", {
                                            className: "mk-pp-mono-sm",
                                            children: "synced both ways"
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsxs("div", {
                                    className: "mk-dw-sync__row",
                                    children: [
                                        /*#__PURE__*/ _jsx("span", {
                                            className: "mk-dw-sync__k",
                                            children: "出"
                                        }),
                                        /*#__PURE__*/ _jsxs("span", {
                                            className: "mk-dw-sync__what",
                                            children: [
                                                /*#__PURE__*/ _jsx("b", {
                                                    children: "Marked Ready in Right Hand DMS"
                                                }),
                                                /*#__PURE__*/ _jsx("span", {
                                                    children: "On your site with photos, specs and the translated sheet"
                                                })
                                            ]
                                        }),
                                        /*#__PURE__*/ _jsx("span", {
                                            className: "mk-dw-sync__state",
                                            children: "Live"
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsxs("div", {
                                    className: "mk-dw-sync__row",
                                    children: [
                                        /*#__PURE__*/ _jsx("span", {
                                            className: "mk-dw-sync__k",
                                            children: "預"
                                        }),
                                        /*#__PURE__*/ _jsxs("span", {
                                            className: "mk-dw-sync__what",
                                            children: [
                                                /*#__PURE__*/ _jsx("b", {
                                                    children: "$500 deposit through your Square"
                                                }),
                                                /*#__PURE__*/ _jsx("span", {
                                                    children: "The car goes on hold on the site, and the buyer is added as a customer"
                                                })
                                            ]
                                        }),
                                        /*#__PURE__*/ _jsx("span", {
                                            className: "mk-dw-sync__state mk-dw-sync__state--amber",
                                            children: "On hold"
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsxs("div", {
                                    className: "mk-dw-sync__row",
                                    children: [
                                        /*#__PURE__*/ _jsx("span", {
                                            className: "mk-dw-sync__k",
                                            children: "問"
                                        }),
                                        /*#__PURE__*/ _jsxs("span", {
                                            className: "mk-dw-sync__what",
                                            children: [
                                                /*#__PURE__*/ _jsx("b", {
                                                    children: "“Can you find me a Hijet dump?”"
                                                }),
                                                /*#__PURE__*/ _jsx("span", {
                                                    children: "The site’s car search form, now a lead waiting for your reply"
                                                })
                                            ]
                                        }),
                                        /*#__PURE__*/ _jsx("span", {
                                            className: "mk-dw-sync__state",
                                            children: "New lead"
                                        })
                                    ]
                                })
                            ]
                        })
                    }),
                    /*#__PURE__*/ _jsxs(ProductSection, {
                        id: "themes",
                        label: "Themes",
                        title: "Five themes, each a live site you can try.",
                        children: [
                            /*#__PURE__*/ _jsx("p", {
                                className: "mk-dw-intro",
                                children: "Open a preview and type your dealership’s name and city into the panel in the corner to see it as yours. Try its palettes too. Photos and prices are samples."
                            }),
                            /*#__PURE__*/ _jsx("div", {
                                className: "mk-dw-themes",
                                children: THEMES.map((th)=>/*#__PURE__*/ _jsxs(Card, {
                                        shadow: "sm",
                                        className: "mk-dw-theme",
                                        "data-testid": Testids.app.marketing.siteTheme(th.id),
                                        children: [
                                            /*#__PURE__*/ _jsxs("div", {
                                                className: "mk-dw-theme__poster",
                                                style: themeStyle(th),
                                                "aria-hidden": "true",
                                                children: [
                                                    /*#__PURE__*/ _jsx("span", {
                                                        className: "mk-dw-theme__bar"
                                                    }),
                                                    /*#__PURE__*/ _jsx("span", {
                                                        className: "mk-dw-theme__headline",
                                                        children: th.headline
                                                    }),
                                                    /*#__PURE__*/ _jsx("span", {
                                                        className: "mk-dw-theme__swatches",
                                                        children: Object.values(th.colors).map((c)=>/*#__PURE__*/ _jsx("span", {
                                                                style: {
                                                                    background: c
                                                                }
                                                            }, c))
                                                    })
                                                ]
                                            }),
                                            /*#__PURE__*/ _jsxs("div", {
                                                className: "mk-dw-theme__body",
                                                children: [
                                                    /*#__PURE__*/ _jsx(Caps, {
                                                        className: "mk-dw-theme__fit",
                                                        children: th.fit
                                                    }),
                                                    /*#__PURE__*/ _jsx("h3", {
                                                        className: "mk-dw-theme__name",
                                                        children: th.name
                                                    }),
                                                    /*#__PURE__*/ _jsx("p", {
                                                        className: "mk-dw-theme__line",
                                                        children: th.line
                                                    }),
                                                    /*#__PURE__*/ _jsx(Button, {
                                                        size: "md",
                                                        variant: "secondary",
                                                        onClick: ()=>open(th),
                                                        "aria-haspopup": "dialog",
                                                        "data-testid": Testids.app.marketing.sitePreviewOpen(th.id),
                                                        children: "Open live preview"
                                                    })
                                                ]
                                            })
                                        ]
                                    }, th.id))
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx("section", {
                        className: "mk-ground--washi",
                        children: /*#__PURE__*/ _jsxs(Container, {
                            className: "mk-pp-section",
                            children: [
                                /*#__PURE__*/ _jsx(SectionLabel, {
                                    k: "内容",
                                    children: "What’s included"
                                }),
                                /*#__PURE__*/ _jsx("h2", {
                                    className: "mk-pp-section__title",
                                    children: "What we build, run and keep in sync."
                                }),
                                /*#__PURE__*/ _jsx("div", {
                                    className: "mk-dw-cards",
                                    children: INCLUDED.map((c)=>/*#__PURE__*/ _jsxs("div", {
                                            className: "mk-dw-card",
                                            children: [
                                                /*#__PURE__*/ _jsx("div", {
                                                    className: "mk-dw-card__k",
                                                    children: c.k
                                                }),
                                                /*#__PURE__*/ _jsx("h3", {
                                                    className: "mk-dw-card__title",
                                                    children: c.title
                                                }),
                                                /*#__PURE__*/ _jsx("p", {
                                                    className: "mk-dw-card__text",
                                                    children: c.text
                                                })
                                            ]
                                        }, c.k))
                                })
                            ]
                        })
                    }),
                    /*#__PURE__*/ _jsx("section", {
                        id: "price",
                        className: "mk-ground--kinari",
                        children: /*#__PURE__*/ _jsx(Container, {
                            className: "mk-dw-price-wrap",
                            children: /*#__PURE__*/ _jsxs(Card, {
                                shadow: "md",
                                className: "mk-dw-price",
                                "data-testid": Testids.app.marketing.dealerWebsitesPrice,
                                children: [
                                    /*#__PURE__*/ _jsxs("div", {
                                        className: "mk-dw-price__head",
                                        children: [
                                            /*#__PURE__*/ _jsx(Caps, {
                                                children: "Add-on for dealership plans"
                                            }),
                                            /*#__PURE__*/ _jsx("h2", {
                                                className: "mk-dw-price__title",
                                                children: "Dealer website"
                                            })
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsxs("div", {
                                        className: "mk-dw-price__amount",
                                        children: [
                                            /*#__PURE__*/ _jsx("span", {
                                                className: "mk-dw-price__n",
                                                ...priced('site.monthly'),
                                                children: t['site.monthly']
                                            }),
                                            /*#__PURE__*/ _jsx("span", {
                                                className: "mk-dw-price__per",
                                                children: "/ month, on top of your plan"
                                            })
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsxs("ul", {
                                        className: "mk-dw-price__lines",
                                        children: [
                                            /*#__PURE__*/ _jsxs("li", {
                                                children: [
                                                    /*#__PURE__*/ _jsx("span", {
                                                        className: "mk-dw-price__label",
                                                        children: "Setup, once"
                                                    }),
                                                    /*#__PURE__*/ _jsx("b", {
                                                        ...priced('site.setup'),
                                                        children: t['site.setup']
                                                    })
                                                ]
                                            }),
                                            /*#__PURE__*/ _jsxs("li", {
                                                children: [
                                                    /*#__PURE__*/ _jsx("span", {
                                                        className: "mk-dw-price__label",
                                                        children: "Setup on the Dealer plan"
                                                    }),
                                                    /*#__PURE__*/ _jsx("b", {
                                                        ...priced('site.dealer'),
                                                        children: t['site.dealer']
                                                    })
                                                ]
                                            }),
                                            /*#__PURE__*/ _jsxs("li", {
                                                children: [
                                                    /*#__PURE__*/ _jsx("span", {
                                                        className: "mk-dw-price__label",
                                                        children: "Change requests a month"
                                                    }),
                                                    /*#__PURE__*/ _jsxs("b", {
                                                        children: [
                                                            "Up to ",
                                                            /*#__PURE__*/ _jsx("span", {
                                                                ...priced('site.edits'),
                                                                children: t['site.edits']
                                                            })
                                                        ]
                                                    })
                                                ]
                                            }),
                                            /*#__PURE__*/ _jsxs("li", {
                                                children: [
                                                    /*#__PURE__*/ _jsx("span", {
                                                        className: "mk-dw-price__label",
                                                        children: "Paid yearly"
                                                    }),
                                                    /*#__PURE__*/ _jsxs("b", {
                                                        children: [
                                                            /*#__PURE__*/ _jsx("span", {
                                                                ...priced('site.annual'),
                                                                children: t['site.annual']
                                                            }),
                                                            " / year"
                                                        ]
                                                    })
                                                ]
                                            })
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsx("p", {
                                        className: "mk-dw-price__note",
                                        children: "Hosting, updates, the two-way sync, Square deposits and embeds are all included. Change requests cover new pages, copy, photos and layout tweaks. Available on the Starter, Shop and Dealer plans. Add it from your plan, or ask on a call."
                                    }),
                                    /*#__PURE__*/ _jsxs("div", {
                                        className: "mk-dw-price__actions",
                                        children: [
                                            /*#__PURE__*/ _jsx(ButtonLink, {
                                                href: MKT.signupFor('dealer'),
                                                ...ctaEvent(AnalyticsEvent.ctaSignup, 'dealer-websites', 'dealer'),
                                                children: "Get started as a dealership"
                                            }),
                                            /*#__PURE__*/ _jsx(ButtonLink, {
                                                href: MKT.bookCall,
                                                variant: "secondary",
                                                ...ctaEvent(AnalyticsEvent.ctaBookCall, 'dealer-websites', 'dealer'),
                                                children: "Book a 20-minute call"
                                            })
                                        ]
                                    })
                                ]
                            })
                        })
                    }),
                    /*#__PURE__*/ _jsx(ProductFaq, {
                        title: "Dealer website questions",
                        items: [
                            [
                                'Is it WordPress?',
                                'Yes. Each theme is a WordPress theme with our plugin, which syncs the site with Right Hand DMS. We host and update it, so you never have to touch WordPress yourself.'
                            ],
                            [
                                'Who holds the deposit money?',
                                'You do. Deposits go through your own Square account and are paid straight to you. Right Hand DMS only records the deposit against the car and the customer.'
                            ],
                            [
                                'Can I use my own domain?',
                                'Yes. We connect the site to the domain your dealership already has registered, and it stays yours.'
                            ],
                            [
                                'Can I get one on Import my own?',
                                'No. Websites are for dealerships, so they’re only available on the Starter, Shop and Dealer plans.'
                            ],
                            [
                                'What counts as a change request?',
                                'Anything you’d ask a web designer to do: a new page, new copy or photos, a section moved, a widget embedded. Inventory changes don’t count, because the sync handles those.'
                            ]
                        ]
                    }),
                    /*#__PURE__*/ _jsx(NextLink, {
                        label: "Next",
                        title: "Plans and pricing",
                        go: "料金 →",
                        href: MKT.pricingFor('dealer')
                    }),
                    /*#__PURE__*/ _jsx(CtaBand, {
                        audience: "dealer"
                    })
                ]
            }),
            preview && /*#__PURE__*/ _jsx(PreviewDialog, {
                preview: preview,
                onClose: ()=>setPreview(null)
            })
        ]
    });
}
/** The live preview in a frame, as a modal dialog. */ function PreviewDialog({ preview, onClose }) {
    const ref = useRef(null);
    const [width, setWidth] = useState('desktop');
    useEffect(()=>{
        const d = ref.current;
        if (d && !d.open) d.showModal();
    }, []);
    return /*#__PURE__*/ _jsxs("dialog", {
        ref: ref,
        className: "mk-dw-dialog",
        "aria-label": `${preview.theme.name} preview`,
        onClose: onClose,
        "data-testid": Testids.app.marketing.sitePreviewDialog,
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "mk-dw-dialog__bar",
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: "mk-dw-dialog__title",
                        children: [
                            /*#__PURE__*/ _jsx("b", {
                                children: preview.theme.name
                            }),
                            /*#__PURE__*/ _jsx("span", {
                                children: "Live preview · sample dealership"
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("fieldset", {
                        className: "mk-dw-dialog__widths",
                        children: [
                            /*#__PURE__*/ _jsx("legend", {
                                className: "mk-dw-sr",
                                children: "Preview width"
                            }),
                            [
                                'desktop',
                                'phone'
                            ].map((w)=>/*#__PURE__*/ _jsx("button", {
                                    type: "button",
                                    "aria-pressed": width === w,
                                    onClick: ()=>setWidth(w),
                                    "data-testid": Testids.app.marketing.sitePreviewWidth(w),
                                    children: w === 'desktop' ? 'Desktop' : 'Phone'
                                }, w))
                        ]
                    }),
                    /*#__PURE__*/ _jsx("button", {
                        type: "button",
                        className: "mk-dw-dialog__close",
                        onClick: ()=>ref.current?.close(),
                        "data-testid": Testids.app.marketing.sitePreviewClose,
                        children: "Close"
                    })
                ]
            }),
            /*#__PURE__*/ _jsx("div", {
                className: "mk-dw-dialog__stage",
                children: /*#__PURE__*/ _jsx("iframe", {
                    title: `${preview.theme.name} theme, live preview`,
                    src: preview.url,
                    className: width === 'phone' ? 'mk-dw-frame mk-dw-frame--phone' : 'mk-dw-frame',
                    "data-testid": Testids.app.marketing.sitePreviewFrame
                }, preview.url)
            })
        ]
    });
}
